/**
 * Sprint 182 — Vault Locked Chip.
 *
 * Subtle blue floating chip that appears when a /api/vault/* endpoint
 * 401s, indicating the Vault TOTP gate is closed. Distinct from the orange
 * AuthDriftBanner (which signals real session drift) — this is purely
 * informational: "your admin session is fine, you just need to unlock the
 * Vault to see this data."
 *
 * Bottom-right placement so it doesn't compete with the AuthDriftBanner
 * (top, full-width). Auto-dismisses after 8s if not interacted with so it
 * doesn't linger after the user has already navigated away.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Lock, X } from 'lucide-react';

export const VaultLockedChip = () => {
  const [visible, setVisible] = useState(false);
  const dismissTimer = useRef(null);

  useEffect(() => {
    const handler = () => {
      setVisible(true);
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
      dismissTimer.current = setTimeout(() => setVisible(false), 8000);
    };
    window.addEventListener('hibff:vault-locked', handler);
    return () => {
      window.removeEventListener('hibff:vault-locked', handler);
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      data-testid="vault-locked-chip"
      role="status"
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 65,
        background: 'rgba(30, 64, 175, 0.96)',
        backdropFilter: 'blur(8px)',
        color: 'white',
        padding: '8px 12px 8px 14px',
        borderRadius: 999,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontSize: 12,
        fontWeight: 600,
        boxShadow: '0 4px 16px rgba(30, 64, 175, 0.35)',
        maxWidth: 340,
        animation: 'hibff-chip-slide-in 0.25s ease-out',
      }}
    >
      <style>{`
        @keyframes hibff-chip-slide-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <Lock size={13} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1, lineHeight: 1.35 }}>
        Vault is locked —{' '}
        <a
          href="/admin/vault"
          data-testid="vault-locked-chip-unlock"
          style={{
            color: '#BFDBFE',
            textDecoration: 'underline',
            textUnderlineOffset: 2,
          }}
        >
          click to unlock
        </a>
      </span>
      <button
        type="button"
        onClick={() => setVisible(false)}
        data-testid="vault-locked-chip-dismiss"
        aria-label="Dismiss vault-locked chip"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'rgba(255,255,255,0.7)',
          cursor: 'pointer',
          padding: 2,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <X size={13} />
      </button>
    </div>
  );
};

export default VaultLockedChip;
